import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  AdminCardPartnerService,
  CardPartnerAccountTotals,
  CardPartnerAdminDetail,
  CardPartnerAdminListKind,
  CardPartnerAdminPage,
  CardPartnerAdminTransaction,
  CardPartnerAdminTransfer
} from '../../../services/admin-card-partner.service';
import {
  cardPartnerAccountColor,
  cardPartnerCardColor,
  cardPartnerChainLabel,
  cardPartnerCopy,
  cardPartnerCrypto,
  cardPartnerKeyOr,
  cardPartnerKycColor,
  cardPartnerMoney,
  cardPartnerNetworkLabel,
  cardPartnerShortHash,
  cardPartnerStatusColor,
  cardPartnerTxAmount,
  cardPartnerTxDirection
} from '../../../utils/card-partner-format';

interface ListState<T> {
  items: T[];
  page: number;
  totalPages: number;
  total: number;
  loading: boolean;
}

function emptyList<T>(): ListState<T> {
  return { items: [], page: 1, totalPages: 0, total: 0, loading: false };
}

/**
 * One cardholder at the card partner, as mirrored in our DB: account (everything
 * but the raw partner row), our linked user, cards, ALL deposit addresses
 * (registered or not — admins see what the partner holds), totals for the
 * selected month and all time, and paged transactions/deposits/withdrawals.
 * "Refresh" pulls this account from the partner now (throttled to once a minute).
 */
@Component({
  selector: 'app-admin-card-partner-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TableDirective,
    BadgeComponent,
    ButtonDirective,
    SpinnerComponent,
    TranslatePipe
  ],
  templateUrl: './admin-card-partner-detail-page.component.html',
  styleUrls: ['../admin-card-partner-page/admin-card-partner-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminCardPartnerDetailPageComponent {
  private logger = new Logger('AdminCardPartnerDetailPageComponent');
  private service = inject(AdminCardPartnerService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  static readonly PAGE_SIZE = 50;

  partnerUserId = '';
  readonly month = this.initialMonth();

  detail = signal<CardPartnerAdminDetail | null>(null);
  loading = signal(true);
  notFound = signal(false);
  refreshing = signal(false);
  refreshHint = signal<string | null>(null);
  copied = signal<string | null>(null);

  transactions = signal<ListState<CardPartnerAdminTransaction>>(emptyList());
  deposits = signal<ListState<CardPartnerAdminTransfer>>(emptyList());
  withdrawals = signal<ListState<CardPartnerAdminTransfer>>(emptyList());

  account = computed(() => this.detail()?.account ?? null);
  totalsRows = computed<{ key: string; totals: CardPartnerAccountTotals }[]>(() => {
    const d = this.detail();
    if (!d) return [];
    const rows = [{ key: 'all_time', totals: d.totals.all_time }];
    if (d.period.month !== 'all') rows.unshift({ key: 'period', totals: d.totals.period });
    return rows;
  });

  constructor() {
    // Follow the route param: the referrer link navigates to another account on
    // the same component instance.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.partnerUserId = params.get('id') ?? '';
      this.detail.set(null);
      this.loading.set(true);
      this.notFound.set(false);
      this.refreshHint.set(null);
      this.transactions.set(emptyList());
      this.deposits.set(emptyList());
      this.withdrawals.set(emptyList());
      this.load();
    });
  }

  // ── actions ─────────────────────────────────────────────────────────────

  back(): void {
    this.router.navigate(['/', this.lang(), 'admin', 'card-partner'], { queryParams: { month: this.month } });
  }

  openUser(id: number): void {
    this.router.navigate(['/', this.lang(), 'admin', 'users', String(id)]);
  }

  openAccount(partnerUserId: string): void {
    this.router.navigate(['/', this.lang(), 'admin', 'card-partner', partnerUserId], { queryParams: { month: this.month } });
  }

  refresh(): void {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    this.refreshHint.set(null);
    this.service.refresh(this.partnerUserId, this.month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: d => {
          this.refreshing.set(false);
          this.detail.set(d);
          if (d.refresh_error) {
            this.refreshHint.set(d.refresh_error === 'not_configured' ? 'card_partner_admin.refresh_not_configured' : 'card_partner_admin.refresh_error');
          } else {
            this.refreshHint.set('card_partner_admin.refresh_done');
          }
          (['transactions', 'deposits', 'withdrawals'] as CardPartnerAdminListKind[]).forEach(k => this.loadList(k, 1));
        },
        error: e => {
          this.refreshing.set(false);
          this.refreshHint.set(e?.status === 429 ? 'card_partner_admin.refresh_throttled' : 'card_partner_admin.refresh_error');
        }
      });
  }

  loadList(kind: CardPartnerAdminListKind, page: number): void {
    const target = this.listSignal(kind);
    target.update(s => ({ ...s, loading: true }));
    this.service.list<any>(this.partnerUserId, kind, page, AdminCardPartnerDetailPageComponent.PAGE_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CardPartnerAdminPage<any>) => target.set({
          items: res.items, page: res.page, totalPages: res.total_pages, total: res.total, loading: false
        }),
        error: err => {
          this.logger.error(`Failed to load ${kind}`, err);
          target.update(s => ({ ...s, loading: false }));
        }
      });
  }

  copy(value: string | null | undefined): void {
    if (!value) return;
    cardPartnerCopy(value).then(ok => {
      if (!ok) return;
      this.copied.set(value);
      setTimeout(() => { if (this.copied() === value) this.copied.set(null); }, 2000);
    });
  }

  // ── display helpers ─────────────────────────────────────────────────────

  keyOr = cardPartnerKeyOr;
  kycColor = cardPartnerKycColor;
  accountColor = cardPartnerAccountColor;
  cardColor = cardPartnerCardColor;
  statusColor = cardPartnerStatusColor;
  chainLabel = cardPartnerChainLabel;
  networkLabel = cardPartnerNetworkLabel;
  shortHash = cardPartnerShortHash;
  txDirection = cardPartnerTxDirection;

  txAmount(t: CardPartnerAdminTransaction): string {
    return cardPartnerTxAmount(t, this.lang());
  }

  txOriginal(t: CardPartnerAdminTransaction): string | null {
    const amount = Number(t.transaction_amount ?? 0);
    if (!amount || !t.transaction_currency || t.transaction_currency === t.currency) return null;
    return `${this.money(amount)} ${t.transaction_currency}`;
  }

  txPlace(t: CardPartnerAdminTransaction): string {
    return [t.merchant_city, t.merchant_country].filter(Boolean).join(', ');
  }

  money(value: string | number | null | undefined): string {
    return cardPartnerMoney(value, this.lang());
  }

  crypto(value: string | null | undefined): string {
    return cardPartnerCrypto(value, this.lang());
  }

  tokenEntries(map: Record<string, string> | undefined): { token: string; value: string }[] {
    return Object.entries(map ?? {}).map(([token, value]) => ({ token, value }));
  }

  date(value: string | null | undefined): string {
    return value ? new Date(value).toLocaleDateString(this.lang()) : '—';
  }

  dateTime(value: string | null | undefined): string {
    return value ? new Date(value).toLocaleString(this.lang(), { dateStyle: 'short', timeStyle: 'short' }) : '—';
  }

  // ── loading ─────────────────────────────────────────────────────────────

  private load(): void {
    this.service.detail(this.partnerUserId, this.month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: d => {
          this.detail.set(d);
          this.loading.set(false);
          (['transactions', 'deposits', 'withdrawals'] as CardPartnerAdminListKind[]).forEach(k => this.loadList(k, 1));
        },
        error: err => {
          this.logger.error('Failed to load card partner account', err);
          this.notFound.set(err?.status === 404);
          this.loading.set(false);
        }
      });
  }

  private listSignal(kind: CardPartnerAdminListKind): WritableSignal<ListState<any>> {
    return kind === 'transactions' ? this.transactions : kind === 'deposits' ? this.deposits : this.withdrawals;
  }

  private initialMonth(): string {
    const m = this.route.snapshot.queryParamMap.get('month');
    if (m && (m === 'all' || /^\d{4}-\d{2}$/.test(m))) return m;
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  }

  private lang(): string {
    try {
      return this.translate.getActiveLang() || localStorage.getItem('language') || 'en';
    } catch {
      return 'en';
    }
  }
}
